import { getUsageCount } from '../utils/usage';
import { offeredUsageSurfaces, type UsageScope } from '../utils/usage-surfaces';
import type { DataIndexUsage, DataIndexUsageKey } from '../utils/types';

/**
 * What the drawer says an item is used by, before any of it is opened.
 *
 * One sentence naming the surfaces that have something on them, so the
 * answer to "is this safe to change?" is readable without expanding
 * anything.
 */

/** How many rows an accordion opens with, and how many more each Load more adds. */
export const USAGE_PAGE_SIZE = 5;

export type UsageSurface = {
  id: DataIndexUsageKey;
  /** What the surface is called, as the workspace calls it. */
  label: string;
  icon: 'segment' | 'campaigns' | 'email' | 'newsletter';
  count: number;
};

export type UsageSurfaceTerms = {
  campaignsTitlePlural: string;
  newslettersTitlePlural: string;
};

/** What each surface is called and what it is drawn with, in Ember's accordion order. */
const ACCORDION: { id: DataIndexUsageKey; label: (terms: UsageSurfaceTerms) => string; icon: UsageSurface['icon'] }[] =
  [
    { id: 'segments', label: () => 'Segments', icon: 'segment' },
    { id: 'campaigns', label: (terms) => terms.campaignsTitlePlural, icon: 'campaigns' },
    { id: 'templates', label: () => 'Templates', icon: 'email' },
    { id: 'design_studio', label: () => 'Design Studio', icon: 'email' },
    { id: 'snippets', label: () => 'Snippets', icon: 'email' },
    { id: 'newsletters', label: (terms) => terms.newslettersTitlePlural, icon: 'newsletter' },
    { id: 'layouts', label: () => 'Layouts', icon: 'email' },
  ];

const ACCORDION_ORDER: DataIndexUsageKey[] = ACCORDION.map((entry) => entry.id);

/**
 * Every surface the drawer lists, in Ember's order, each counted. Surfaces
 * the plan or the workspace does not have are absent rather than empty.
 */
export function usageSurfaces(usage: DataIndexUsage, scope: UsageScope, terms: UsageSurfaceTerms): UsageSurface[] {
  const offered = new Set(offeredUsageSurfaces(ACCORDION_ORDER, scope));

  return ACCORDION.filter((entry) => offered.has(entry.id)).map((entry) => ({
    id: entry.id,
    label: entry.label(terms),
    icon: entry.icon,
    count: getUsageCount(usage, entry.id),
  }));
}

/**
 * The surfaces as a sentence reads them, which is not the order the accordion
 * lists them in: the page this replaces names newsletters third, before the
 * content surfaces (`usage-and-activity/index.gts:280-312`).
 */
const SENTENCE_ORDER: DataIndexUsageKey[] = [
  'segments',
  'campaigns',
  'newsletters',
  'templates',
  'design_studio',
  'snippets',
  'layouts',
];

/** "Segments, Campaigns, and Templates" — the product's own list format. */
function sentence(surfaces: readonly UsageSurface[]): string {
  const ordered = [...surfaces].sort((a, b) => SENTENCE_ORDER.indexOf(a.id) - SENTENCE_ORDER.indexOf(b.id));

  return new Intl.ListFormat('en-US', { type: 'conjunction' }).format(ordered.map((surface) => surface.label));
}

/** What the item is used in today, or nothing when nothing uses it. */
export function currentlyUsedIn(surfaces: readonly UsageSurface[]): string | null {
  const used = surfaces.filter((surface) => surface.count > 0);

  return used.length === 0 ? null : sentence(used);
}

/** "Attributes can be used in Segments, Campaigns, and Newsletters." */
export function couldBeUsedIn(surfaces: readonly UsageSurface[]): string {
  return sentence(surfaces);
}
